import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  MessageCircle, 
  Plus, 
  Search, 
  Edit3,
  MoreHorizontal
} from 'lucide-react';

interface ChatListDesignProps {
  title?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onNewChat?: () => void;
  loading?: boolean;
  children: React.ReactNode;
}

const ChatListDesign: React.FC<ChatListDesignProps> = ({
  title = "Chat",
  showSearch = true,
  searchValue = "",
  onSearchChange,
  onNewChat,
  loading = false,
  children
}) => {
  return (
    <Card className="glass shadow-card border-border/30 h-full flex flex-col overflow-hidden backdrop-blur-xl">
      {/* Header */}
      <CardHeader className="pb-4 border-b border-border/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl gradient-primary">
              <MessageCircle className="w-5 h-5 text-primary-foreground" />
            </div>
            <h2 className="text-xl font-bold gradient-text">
              {title}
            </h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 rounded-full hover:bg-primary/10 transition-smooth"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            {onNewChat && (
              <Button
                onClick={onNewChat}
                size="sm"
                className="h-9 w-9 p-0 rounded-full gradient-primary shadow-primary/20 shadow-lg hover:shadow-primary/30 transition-smooth"
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        {showSearch && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca chat..."
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="pl-10 glass border-border/30 focus:ring-2 focus:ring-primary/20 transition-smooth"
            />
          </div>
        )}
      </CardHeader>
      
      {/* Content */}
      <CardContent className="flex-1 overflow-hidden p-0">
        <div className="h-full overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border/30">
          {loading ? (
            <div className="space-y-3 p-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-4 w-8" />
                </div>
              ))}
            </div>
          ) : (
            children
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatListDesign;